
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

type AuthContextType = {
  session: Session | null;
  user: User | null;
  signIn: (email: string, password: string) => Promise<{
    error: Error | null;
    data: { session: Session | null; user: User | null } | null;
  }>;
  signUp: (email: string, password: string, firstName: string, lastName: string, acceptTerms: boolean) => Promise<{
    error: Error | null;
    data: { session: Session | null; user: User | null } | null;
  }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{
    error: Error | null;
    data: any;
  }>;
  loading: boolean;
  supabase: typeof supabase;
  userCountry: string;
  userState: string;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userCountry, setUserCountry] = useState<string>('CA'); // Default to Canada
  const [userState, setUserState] = useState<string>('ON');    // Default to Ontario
  const navigate = useNavigate();

  // Function to set default location to Ontario, Canada
  const setDefaultLocation = () => {
    setUserCountry('CA');
    setUserState('ON');
  };

  // Function to detect user's country (simplified)
  const detectUserLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (response.ok) {
        const locationData = await response.json();
        
        const supportedCountries = ['CA', 'US', 'GB', 'IN'];
        const countryCode = locationData.country || 'CA';
        
        if (supportedCountries.includes(countryCode)) {
          setUserCountry(countryCode);
          let regionCode = locationData.region_code;
          if (regionCode) {
            setUserState(regionCode);
          } else if (countryCode === 'CA') {
            setUserState('ON');
          }
          return { country: countryCode, state: regionCode || (countryCode === 'CA' ? 'ON' : '') };
        }
        
        setDefaultLocation();
        return { country: 'CA', state: 'ON' };
      }
    } catch (error) {
      console.error("Error detecting location:", error);
    }
    
    setDefaultLocation();
    return { country: 'CA', state: 'ON' };
  };

  // Function to update user's profile with location
  const updateUserLocation = async (userId: string, countryCode: string, stateCode: string) => {
    try {
      const { data: countryData } = await supabase
        .from('countries')
        .select('id')
        .eq('code', countryCode)
        .single();
      
      if (!countryData) return;
      
      const { data: stateData } = await supabase
        .from('states')
        .select('id')
        .eq('code', stateCode)
        .eq('country_id', countryData.id)
        .single();
      
      let stateId = stateData?.id;
      if (!stateId) {
        const { data: defaultState } = await supabase
          .from('states')
          .select('id')
          .eq('country_id', countryData.id)
          .eq('code', countryCode === 'CA' ? 'ON' : stateCode)
          .single();
          
        if (defaultState) {
          stateId = defaultState.id;
        } else {
          const { data: anyState } = await supabase
            .from('states')
            .select('id')
            .eq('country_id', countryData.id)
            .limit(1)
            .single();
            
          if (anyState) {
            stateId = anyState.id;
          }
        }
      }
      
      await supabase
        .from('profiles')
        .update({ 
          country_id: countryData.id,
          state_id: stateId
        })
        .eq('id', userId);
    } catch (error) {
      console.error("Error updating user location:", error);
    }
  };

  // Function to fetch user's saved location from profile
  const fetchUserLocation = async (userId: string) => {
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select(`
          country_id,
          state_id,
          countries:country_id (code),
          states:state_id (code)
        `)
        .eq('id', userId)
        .single();

      if (error || !profileData) {
        console.error("Error fetching user location:", error);
        setDefaultLocation();
        return null;
      }

      if (profileData.countries?.code) {
        setUserCountry(profileData.countries.code);
      } else {
        setUserCountry('CA');
      }
      
      if (profileData.states?.code) {
        setUserState(profileData.states.code);
      } else {
        setUserState('ON');
      }
      
      return { country: profileData.countries?.code || 'CA', state: profileData.states?.code || 'ON' };
    } catch (error) {
      console.error("Error in fetchUserLocation:", error);
      setDefaultLocation();
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        console.log("Initializing auth...");
        
        // Get initial session
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (mounted) {
          console.log("Initial session:", !!initialSession?.user);
          setSession(initialSession);
          setUser(initialSession?.user ?? null);
          
          if (initialSession?.user) {
            // Handle location setup for authenticated users in background
            setTimeout(async () => {
              try {
                const savedLocation = await fetchUserLocation(initialSession.user.id);
                if (!savedLocation) {
                  const detectedLocation = await detectUserLocation();
                  await updateUserLocation(
                    initialSession.user.id, 
                    detectedLocation.country, 
                    detectedLocation.state
                  );
                }
              } catch (error) {
                console.error("Error setting up user location:", error);
              }
            }, 0);
          } else {
            setDefaultLocation();
          }
          
          setLoading(false);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (mounted) {
          setDefaultLocation();
          setLoading(false);
        }
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log("Auth state change event:", event, "session:", !!currentSession?.user);
        
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          
          if (currentSession?.user && event === 'SIGNED_IN') {
            // Handle location setup in background for new sign-ins
            setTimeout(async () => {
              try {
                const savedLocation = await fetchUserLocation(currentSession.user.id);
                if (!savedLocation) {
                  const detectedLocation = await detectUserLocation();
                  await updateUserLocation(
                    currentSession.user.id, 
                    detectedLocation.country, 
                    detectedLocation.state
                  );
                }
              } catch (error) {
                console.error("Error setting up user location:", error);
              }
            }, 0);
          } else if (!currentSession?.user) {
            setDefaultLocation();
          }
          
          // Ensure loading is false after auth state changes
          setLoading(false);
        }
      }
    );

    // Initialize auth
    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string,
    acceptTerms: boolean
  ) => {
    try {
      const { data: signUpCheckData, error: signUpCheckError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false
        }
      });
      
      if (!signUpCheckError) {
        return { 
          error: new Error("User already registered with this email."), 
          data: null 
        };
      }
      
      try {
        const { data: existingProfiles } = await supabase
          .from('profiles')
          .select('id')
          .ilike('email', email)
          .maybeSingle();
          
        if (existingProfiles) {
          return { 
            error: new Error("User already registered with this email."), 
            data: null 
          };
        }
      } catch (profileCheckError) {
        console.error("Error checking profiles:", profileCheckError);
      }

      if (!acceptTerms) {
        return {
          error: new Error("You must accept the terms and conditions to create an account."),
          data: null
        };
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo: `${window.location.origin}/auth?confirmation=true`,
        }
      });

      if (error) {
        return { error, data: null };
      }

      if (data.user && acceptTerms) {
        const { error: termsError } = await supabase
          .from('terms_acceptance')
          .insert({
            user_id: data.user.id,
            terms_version: '1.0',
            marketing_consent: true
          });

        if (termsError) {
          console.error('Failed to record terms acceptance:', termsError);
        }
      }

      return { data, error: null };
    } catch (error) {
      return { error: error as Error, data: null };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log("Attempting sign in...");
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!error && data.session) {
        console.log("Sign in successful, navigating to dashboard");
        navigate('/dashboard');
      }

      return { data, error };
    } catch (error) {
      console.error("Sign in error:", error);
      return { error: error as Error, data: null };
    }
  };

  const signOut = async () => {
    try {
      console.log("Signing out...");
      setUser(null);
      setSession(null);
      
      await supabase.auth.signOut().catch(error => {
        console.warn("Error during sign out:", error);
      });
      
      navigate('/');
    } catch (error) {
      console.error("Unexpected error during sign out:", error);
      navigate('/');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      console.log("Resetting password for:", email);
      
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?type=recovery`,
      });
      
      console.log("Reset password response:", { data, error });
      return { data, error };
    } catch (error) {
      console.error("Reset password error:", error);
      return { error: error as Error, data: null };
    }
  };

  const value = {
    session,
    user,
    signIn,
    signUp,
    signOut,
    resetPassword,
    loading,
    supabase,
    userCountry,
    userState
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
